import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
// Import the Playbook type from the features/courses/types which is what transformPlaybookData returns
import { Playbook as TransformedCourseType } from '@/features/courses/types'; 
import { Database } from '@/integrations/supabase/types';
// Import the transformation function and the raw static data for task updates
import { transformPlaybookData } from '@/features/courses/services/dataTransformService';
import { breakthroughCycles as rawStaticCourses } from '@/data/playbookCyclesData';
import { transformedPlaybooks } from '@/features/courses/data/transformedPlaybooks';

// Define the structure for user progress from our new Supabase table
export interface UserCourseProgress {
  id: string; // UUID from Supabase
  user_id: string;
  course_id: string; // Corresponds to Course.id from static data
  current_phase_id?: string | null;
  completed_task_ids: string[] | null; // Adjusted to match Supabase type
  overall_status: string | null; // Adjusted to match Supabase type (string instead of specific literals)
  started_at?: string | null;
  last_active_at?: string | null;
  completed_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

// Combined type for a course with its progress
export interface CourseWithProgress extends TransformedCourseType {
  userProgress?: UserCourseProgress | null;
}

export function useCourses() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const getTransformedCourses = (): TransformedCourseType[] => {
    return transformedPlaybooks; // Use the static pre-transformed data that includes our custom titles/descriptions
  };

  // Get all courses and merge with user progress
  const getCourses = async (): Promise<CourseWithProgress[]> => {
    const transformedCourses = getTransformedCourses();
    if (!user) return transformedCourses.map(course => ({ ...course, userProgress: null }));

    const { data: userProgressData, error: userProgressError } = await supabase
      .from('user_course_progress')
      .select('*')
      .eq('user_id', user.id);

    if (userProgressError) {
      console.error('Error fetching user course progress:', userProgressError);
      // Return static courses without progress on error
      return transformedCourses.map(course => ({ ...course, userProgress: null }));
    }

    const coursesWithDataAndProgress = transformedCourses.map(course => {
      const progress = userProgressData?.find(p => p.course_id === course.id) || null;
      return { ...course, userProgress: progress }; 
    });

    return coursesWithDataAndProgress;
  };

  // Get single course details and merge with user progress
  const getCourseDetails = async (courseId: string): Promise<CourseWithProgress | null> => {
    const transformedCourses = getTransformedCourses();
    const course = transformedCourses.find(c => c.id === courseId);
    if (!course) {
      console.error(`Course with id ${courseId} not found after transformation.`);
      return null;
    }

    if (!user) return { ...course, userProgress: null };

    const { data: userProgressData, error: userProgressError } = await supabase
      .from('user_course_progress')
      .select('*')
      .eq('user_id', user.id)
      .eq('course_id', courseId)
      .maybeSingle(); // Expect at most one row

    if (userProgressError) {
      console.error(`Error fetching user progress for course ${courseId}:`, userProgressError);
      return { ...course, userProgress: null }; // Return course without progress on error
    }

    return { ...course, userProgress: userProgressData || null };
  };

  // Placeholder for updateTaskProgress mutation
  const useUpdateTaskProgress = () => {
    return useMutation<UserCourseProgress | null, Error, { courseId: string; phaseId: string; taskId: string; isCompleted: boolean }>({
      mutationFn: async (variables: { courseId: string; phaseId: string; taskId: string; isCompleted: boolean }) => {
        const { courseId, phaseId, taskId, isCompleted } = variables;
        if (!user) throw new Error('User not authenticated');
        
        // Find the raw static course data to count total tasks, as transformed data might change structure
        const rawCourseStaticData = rawStaticCourses.find(c => c.id === courseId);
        if (!rawCourseStaticData) throw new Error(`Raw course with ID ${courseId} not found.`);

        let { data: currentProgress, error: fetchError } = await supabase
          .from('user_course_progress')
          .select('*')
          .eq('user_id', user.id)
          .eq('course_id', courseId)
          .maybeSingle();

        if (fetchError && fetchError.code !== 'PGRST116') { 
          console.error('Error fetching current progress:', fetchError);
          throw fetchError;
        }
        
        const now = new Date().toISOString();
        let progressToUpdate: UserCourseProgress;

        if (!currentProgress) {
          progressToUpdate = {
            id: 'temp-id', 
            user_id: user.id,
            course_id: courseId,
            current_phase_id: phaseId,
            completed_task_ids: isCompleted ? [taskId] : [],
            overall_status: isCompleted ? 'in-progress' : 'not-started',
            started_at: isCompleted ? now : null,
            last_active_at: now,
            completed_at: null,
            created_at: now, 
            updated_at: now, 
          };
        } else {
          progressToUpdate = { ...currentProgress };
          progressToUpdate.last_active_at = now;
          progressToUpdate.current_phase_id = phaseId;

          let updatedCompletedTaskIds = [...(progressToUpdate.completed_task_ids || [])];
          if (isCompleted) {
            if (!updatedCompletedTaskIds.includes(taskId)) updatedCompletedTaskIds.push(taskId);
          } else {
            updatedCompletedTaskIds = updatedCompletedTaskIds.filter(id => id !== taskId);
          }
          progressToUpdate.completed_task_ids = updatedCompletedTaskIds;

          const totalTasksInCourse = rawCourseStaticData.phases.reduce((acc, p) => {
            // Filter out Pro Tip tasks to be consistent with other progress calculations
            const nonProTipTasks = p.tasks.filter(task => task.title !== 'Pro Tip');
            return acc + nonProTipTasks.length;
          }, 0);
          
          if (updatedCompletedTaskIds.length === 0) {
            progressToUpdate.overall_status = 'not-started';
            progressToUpdate.started_at = null;
            progressToUpdate.completed_at = null;
          } else if (updatedCompletedTaskIds.length === totalTasksInCourse) {
            progressToUpdate.overall_status = 'completed';
            progressToUpdate.completed_at = now;
            if (!progressToUpdate.started_at) progressToUpdate.started_at = now;
          } else {
            progressToUpdate.overall_status = 'in-progress';
            if (!progressToUpdate.started_at) progressToUpdate.started_at = now;
            progressToUpdate.completed_at = null;
          }
        }
        
        const { id, ...upsertData } = progressToUpdate.id === 'temp-id' ? progressToUpdate : { id: null, ...progressToUpdate };
        
        const { data, error } = await supabase
            .from('user_course_progress')
            .upsert(upsertData as any) // Use upsertData which excludes temp-id or actual id
            .select()
            .single();

        if (error) {
            console.error("Error upserting course progress: ", error);
            throw error;
        }
        return data as UserCourseProgress;
      },
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries({ queryKey: ['courses', user?.id] });
        queryClient.invalidateQueries({ queryKey: ['courseDetails', variables.courseId, user?.id] });
      },
      onError: (error) => {
        console.error('Error in updateTaskProgress mutation:', error);
      },
    });
  };

  // Get courses query
  const useGetCourses = () => {
    return useQuery<CourseWithProgress[]>({
      queryKey: ['courses'],
      queryFn: async () => {
        // Use the current transformation function instead of static data
        return getCourses();
      }
    });
  };

  // Get course details query
  const useGetCourseDetails = (courseId: string) => {
    return useQuery<CourseWithProgress>({
      queryKey: ['courses', courseId],
      queryFn: async () => {
        // Find the course in the static data
        const course = transformedPlaybooks.find(c => c.id === courseId);
        if (!course) {
          throw new Error(`Course with ID ${courseId} not found`);
        }
        return course;
      },
      enabled: Boolean(courseId)
    });
  };

  return {
    useGetCourses: () => useGetCourses(),
    useGetCourseDetails: (courseId: string) => useGetCourseDetails(courseId),
    updateTaskProgress: useUpdateTaskProgress(),
  };
} 